'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["superCategory"];
import { useMemo, Fragment, useCallback, useRef, useState, useEffect } from 'react';
import { findDOMNode } from 'react-dom';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import Immutable from 'immutable';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import H4 from 'UIComponents/elements/headings/H4';
import UIPaginator from 'UIComponents/paginator/UIPaginator';
import SortDropdown from 'marketplace-ui-apps-core/results/components/SortDropdown';
import { isFulfilled, isSucceeded, isFailed } from 'marketplace-ui-common/requestStates/RequestStateUtils';
import { SEARCH_RESULTS_MAX } from 'marketplace-ui-common/constants/search';
import * as ScreenSizes from 'marketplace-ui-common/layout/constants/ScreenSizes';
import { useResponsiveProps } from 'marketplace-ui-common/hooks/useResponsiveProps';
import MediaQuery from 'marketplace-ui-common/layout/components/MediaQuery';
import ResultsMaxWarning from 'marketplace-ui-common/storefront/components/ResultsMaxWarning';
import ResultsSection from 'ecosystem-marketplace-core/components/storefront/ResultsSection';
import ScrollToResults from 'ecosystem-marketplace-core/components/storefront/ScrollToResults';
import StorefrontResultsHeader from 'ecosystem-marketplace-core/components/storefront/StorefrontResultsHeader';
import * as CustomPropTypes from '../../../constants/PropTypes';
import * as FilterKey from '../../../constants/search/FilterKey';
import { DEFAULT_SEARCH_LENGTH, NAV_BAR_HEIGHT_INT } from '../../../constants';
import SearchResult from '../../../records/SearchResult';
import AppCollectionsResultsSection from '../../../storefront/components/AppCollectionsResultsSection';
import RecommendedResultsSection from '../../../storefront/components/RecommendedResultsSection';
import { getFiltersHeadingI18nKey } from '../../../utils/i18n';
import MobileFilterPanelNavigator from './mobile/MobileFilterPanelNavigator';
import PageOneLink from './PageOneLink';
import ResultsCount from './ResultsCount';
import ResultsFilters from './ResultsFilters';
import ResultsGrid from './ResultsGrid';
import ResultsHeaderTitle from './ResultsHeaderTitle';
import SupplementalResultsSection from './SupplementalResultsSection';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getI18nKey = key => `appMarketplace.storefront.results.resultsPage.${key}`;
const renderSupplementalResultsSection = ({
  index,
  onResultClick,
  supplementalSearchResult,
  supplementalResultProps,
  trackInteraction
}) => /*#__PURE__*/_jsx(SupplementalResultsSection, {
  onResultClick: (result, resultIndex) => onResultClick(result, resultIndex, supplementalSearchResult.items.size),
  resultProps: supplementalResultProps,
  searchResult: supplementalSearchResult,
  trackInteraction: trackInteraction
}, `supplemental-results-${index}`);

// We only want to send the tracking event once in total, not once per render.
let sentScrollToResultsTrackingEvent = false;
const ResultsPage = ({
  activePage,
  description,
  emptyStateComponent: EmptyStateComponent,
  failedComponent: FailedComponent,
  filterable,
  filters,
  isAllCategoriesHomepage,
  offset,
  onUpdateFilters,
  onClearFilters,
  onFilterChange,
  onPageChange,
  onResultClick,
  onSortChange,
  requestState,
  resultProps,
  resultsPerPage,
  searchResult,
  showRecommendedResultsSection,
  sortable,
  supplementalResults,
  title,
  trackInteraction
}) => {
  const {
    [FilterKey.SUPER_CATEGORY]: superCategory,
    [FilterKey.CATEGORY]: category,
    [FilterKey.SEARCH]: searchValue
  } = filters;
  const {
    appCollectionsSectionClassName
  } = useResponsiveProps({
    appCollectionsSectionClassName: {
      [ScreenSizes.SM]: 'm-top-10 p-top-10',
      [ScreenSizes.MD]: 'm-top-10 p-bottom-0 p-top-10'
    }
  });
  const resultsRef = useRef(null);
  const {
    items: searchResults,
    total: searchResultsTotal
  } = searchResult;
  const searchResultsLength = searchResults.size;
  const hasResults = !!searchResultsTotal;

  // TODO cleanup/refactor
  const [allCategoriesResult = new SearchResult(), restSupplementalResults] = useMemo(() => {
    if (category || superCategory) {
      return [supplementalResults.first(), supplementalResults.rest()];
    }
    return [undefined, supplementalResults];
  }, [category, superCategory, supplementalResults]);
  const supplementalResultProps = useMemo(() => {
    const rest = _objectWithoutPropertiesLoose(resultProps, _excluded);
    return rest;
  }, [resultProps]);
  const {
    filters: allCategoriesFilters,
    items: allCategoriesResults,
    total: allCategoriesTotal
  } = allCategoriesResult;
  const allCategoriesResultsLength = allCategoriesResults.size;
  const hasAllCategoriesResults = !!allCategoriesTotal;
  const showAllCategoriesSearchResults = !!(searchValue && !hasResults && hasAllCategoriesResults);
  const handlePageChange = useCallback(({
    target: {
      value
    }
  }) => {
    onPageChange(value, showAllCategoriesSearchResults ? allCategoriesFilters : filters);
  }, [allCategoriesFilters, filters, onPageChange, showAllCategoriesSearchResults]);
  const [resultsLength, resultsTotal, resultsPageCount] = useMemo(() => {
    let length;
    let total;
    if (showAllCategoriesSearchResults) {
      length = allCategoriesResultsLength;
      total = allCategoriesTotal;
    } else {
      length = searchResultsLength;
      total = searchResultsTotal;
    }
    total = Math.min(total, SEARCH_RESULTS_MAX);
    return [length, total, Math.ceil(total / resultsPerPage)];
  }, [allCategoriesResultsLength, allCategoriesTotal, resultsPerPage, searchResultsLength, searchResultsTotal, showAllCategoriesSearchResults]);
  const filtersHeadingI18nKey = useMemo(() => getFiltersHeadingI18nKey(filters), [filters]);
  const renderedResultsHeaderTitle = title || /*#__PURE__*/_jsx(ResultsHeaderTitle, {
    category: category,
    searchValue: searchValue,
    superCategory: superCategory
  });
  const renderedResultsCount = isFulfilled(requestState) ? /*#__PURE__*/_jsx(ResultsCount, {
    activePage: activePage,
    filtersHeadingI18nKey: filtersHeadingI18nKey,
    hasResults: hasResults,
    offset: offset,
    resultsLength: resultsLength,
    resultsTotal: resultsTotal,
    searchValue: searchValue,
    showAllCategoriesSearchResults: showAllCategoriesSearchResults
  }) : null;
  const validFilterTagKeys = filters.getValidFilterTagKeys();
  const renderedDefaultFilterBar = !validFilterTagKeys.isEmpty() ? /*#__PURE__*/_jsx(ResultsFilters, {
    filters: filters,
    onClearFilters: onClearFilters,
    onFilterChange: onFilterChange,
    trackInteraction: trackInteraction,
    validFilterTagKeys: validFilterTagKeys
  }) : null;
  const renderedResultsFilters = filterable ? /*#__PURE__*/_jsx(MediaQuery, {
    maxScreenSize: ScreenSizes.MD,
    alternate: renderedDefaultFilterBar,
    children: /*#__PURE__*/_jsx(ResultsFilters, {
      filters: filters,
      isMobile: true,
      onClearFilters: onClearFilters,
      onFilterChange: onFilterChange,
      onSortChange: onSortChange,
      trackInteraction: trackInteraction,
      validFilterTagKeys: validFilterTagKeys,
      startSlot: /*#__PURE__*/_jsx(MobileFilterPanelNavigator, {
        filters: filters,
        onUpdateFilters: onUpdateFilters,
        searchTerm: searchValue,
        trackInteraction: trackInteraction
      })
    })
  }) : null;
  const computedSortable = useMemo(() => {
    if (!sortable || !(hasResults || showAllCategoriesSearchResults)) {
      return false;
    }
    return true;
  }, [hasResults, showAllCategoriesSearchResults, sortable]);
  const renderedSortDropdown = computedSortable ? /*#__PURE__*/_jsx(SortDropdown, {
    filters: filters,
    onSortChange: (field, order) => {
      onSortChange(field, order);
      trackInteraction('change sort', {
        sortField: field,
        sortOrder: order
      }, 'storefront-change-sort');
    },
    onShowPreferredLanguageFirstChange: checked => {
      onFilterChange('showPreferredLanguageFirst', checked);
    }
  }) : null;
  const renderedSupplementalResults = useMemo(() => {
    if (!isSucceeded(requestState) || !searchValue || restSupplementalResults.isEmpty()) {
      return null;
    }
    return restSupplementalResults.valueSeq().map((supplementalSearchResult, index) => renderSupplementalResultsSection({
      index,
      onResultClick,
      supplementalSearchResult,
      supplementalResultProps,
      trackInteraction
    })).toArray();
  }, [onResultClick, requestState, restSupplementalResults, searchValue, supplementalResultProps, trackInteraction]);
  const renderResultsGrid = useCallback(searchResultsArray => /*#__PURE__*/_jsx(ResultsGrid, {
    className: "vr-hidden",
    onResultClick: (result, index) => onResultClick(result, index, searchResultsLength),
    requestState: requestState,
    resultProps: resultProps,
    results: searchResultsArray
  }), [onResultClick, requestState, resultProps, searchResultsLength]);
  let renderedPageContent;
  if (isFailed(requestState) && FailedComponent) {
    renderedPageContent = /*#__PURE__*/_jsx(FailedComponent, {});
  } else if (isSucceeded(requestState) && !(hasResults || showAllCategoriesSearchResults)) {
    renderedPageContent = EmptyStateComponent ? /*#__PURE__*/_jsx(EmptyStateComponent, {}) : /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsxs(ResultsSection, {
        gutterSize: "xl",
        children: [/*#__PURE__*/_jsx(H4, {
          children: searchValue ? /*#__PURE__*/_jsx(FormattedJSXMessage, {
            message: getI18nKey('noResultsEmptyState.searchTitle_jsx'),
            elements: {
              b: 'span'
            },
            options: {
              searchValue,
              pageHeading: unescapedText(filtersHeadingI18nKey)
            }
          }) : /*#__PURE__*/_jsx(FormattedMessage, {
            message: getI18nKey('noResultsEmptyState.filterTitle'),
            options: {
              pageHeading: unescapedText(filtersHeadingI18nKey)
            }
          })
        }), /*#__PURE__*/_jsx("p", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: getI18nKey('noResultsEmptyState.message')
          })
        }), activePage > 1 && /*#__PURE__*/_jsx("p", {
          children: /*#__PURE__*/_jsx(PageOneLink, {})
        })]
      }), renderedSupplementalResults]
    });
  } else {
    const renderedResultsGrid = isAllCategoriesHomepage ? /*#__PURE__*/_jsxs(Fragment, {
      children: [renderResultsGrid(searchResults.slice(0, 6)), /*#__PURE__*/_jsx(AppCollectionsResultsSection, {
        className: appCollectionsSectionClassName,
        trackInteraction: trackInteraction
      }), renderResultsGrid(searchResults.slice(6))]
    }) : renderResultsGrid(searchResults);
    const showMaxWarning = activePage >= resultsPageCount && resultsTotal >= SEARCH_RESULTS_MAX;
    const renderedResultsPagination = isSucceeded(requestState) && resultsTotal > resultsLength ? /*#__PURE__*/_jsx(UIPaginator, {
      onPageChange: handlePageChange,
      page: activePage,
      pageCount: resultsPageCount
    }) : null;
    renderedPageContent = /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsx(ResultsSection, {
        ref: resultsRef,
        children: renderedResultsGrid
      }), showMaxWarning && /*#__PURE__*/_jsx(ResultsSection, {
        className: "flex-row justify-center",
        children: /*#__PURE__*/_jsx(ResultsMaxWarning, {})
      }), /*#__PURE__*/_jsx(ResultsSection, {
        children: renderedResultsPagination
      })]
    });
  }
  const [scrollToResultsOpacity, setScrollToResultsOpacity] = useState(0);
  const [scrollToResultsFixed, setScrollToResultsFixed] = useState(true);
  const resultsEl = findDOMNode(resultsRef.current);
  const renderedScrollToResults = resultsEl && searchResultsLength ? /*#__PURE__*/_jsx(ScrollToResults, {
    containerWidth: resultsEl.offsetWidth,
    onClick: () => trackInteraction('click scroll to results', {}, 'interaction'),
    opacity: scrollToResultsOpacity,
    fixed: scrollToResultsFixed
  }) : null;
  if (!sentScrollToResultsTrackingEvent && scrollToResultsOpacity > 0) {
    trackInteraction('view scroll to results', {}, 'interaction');
    sentScrollToResultsTrackingEvent = true;
  }
  const detectResultsVisible = useCallback(() => {
    const rect = resultsEl && resultsEl.getBoundingClientRect();
    const resultsParentRect = resultsEl && resultsEl.parentElement && resultsEl.parentElement.getBoundingClientRect();
    if (rect && resultsParentRect) {
      setScrollToResultsFixed(resultsParentRect.bottom > window.innerHeight);
      setScrollToResultsOpacity((NAV_BAR_HEIGHT_INT - rect.bottom) / 200);
    }
  }, [resultsEl]);
  useEffect(() => {
    window.addEventListener('scroll', detectResultsVisible);
    return () => {
      window.removeEventListener('scroll', detectResultsVisible);
    };
  }, [detectResultsVisible]);
  useEffect(() => {
    // Update scroll to results visibility on re-render
    detectResultsVisible();
  });
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsxs(MediaQuery, {
      maxScreenSize: ScreenSizes.SM,
      children: [showRecommendedResultsSection && /*#__PURE__*/_jsx(RecommendedResultsSection, {
        onResultClick: onResultClick
      }), /*#__PURE__*/_jsx(StorefrontResultsHeader, {
        title: renderedResultsHeaderTitle,
        description: description,
        renderedResultsFilters: renderedResultsFilters,
        renderedSortDropdown: renderedSortDropdown,
        resultsCount: renderedResultsCount,
        searchTerm: searchValue,
        ungatedMobile: true
      })]
    }), renderedPageContent, renderedScrollToResults]
  });
};
ResultsPage.propTypes = {
  activePage: PropTypes.number.isRequired,
  description: PropTypes.node,
  emptyStateComponent: PropTypes.func,
  failedComponent: PropTypes.func,
  filterable: PropTypes.bool,
  filters: CustomPropTypes.storefrontFilters.isRequired,
  isAllCategoriesHomepage: PropTypes.bool,
  offset: PropTypes.number,
  onClearFilters: PropTypes.func.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onResultClick: PropTypes.func.isRequired,
  onSortChange: PropTypes.func.isRequired,
  requestState: CustomPropTypes.requestState.isRequired,
  resultProps: PropTypes.object,
  resultsPerPage: PropTypes.number,
  searchResult: PropTypes.instanceOf(SearchResult).isRequired,
  showRecommendedResultsSection: PropTypes.bool,
  sortable: PropTypes.bool,
  supplementalResults: ImmutablePropTypes.orderedSetOf(PropTypes.instanceOf(SearchResult)),
  title: PropTypes.node,
  trackInteraction: PropTypes.func.isRequired,
  onUpdateFilters: PropTypes.func.isRequired
};
ResultsPage.defaultProps = {
  offset: 0,
  resultProps: {},
  resultsPerPage: DEFAULT_SEARCH_LENGTH,
  sortable: true,
  supplementalResults: new Immutable.OrderedSet()
};
export default ResultsPage;